import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

const ABOUT_URL = 'https://jointhebeacon.com/about'
const SHARE_TEXT = "I joined The Beacon - a free app for staying connected with the people who matter most to you. Simple check-ins, no pressure. It might be the most important thing you do today."

const PLATFORMS = [
  { label: 'Twitter / X', bg: 'bg-black hover:bg-stone-800', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(ABOUT_URL)}` },
  { label: 'Facebook', bg: 'bg-blue-600 hover:bg-blue-700', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ABOUT_URL)}` },
  { label: 'LinkedIn', bg: 'bg-blue-700 hover:bg-blue-800', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(ABOUT_URL)}` },
  { label: 'Reddit', bg: 'bg-orange-600 hover:bg-orange-700', href: `https://reddit.com/submit?url=${encodeURIComponent(ABOUT_URL)}&title=${encodeURIComponent('The Beacon - stay connected with the people who matter')}` },
  { label: 'WhatsApp', bg: 'bg-green-600 hover:bg-green-700', href: `https://wa.me/?text=${encodeURIComponent(SHARE_TEXT + ' ' + ABOUT_URL)}` },
  { label: 'TikTok', bg: 'bg-stone-950 hover:bg-stone-800', href: 'https://www.tiktok.com' },
  { label: 'SMS / Text', bg: 'bg-stone-600 hover:bg-stone-500', href: `sms:?&body=${encodeURIComponent(SHARE_TEXT + ' ' + ABOUT_URL)}` },
]

const STEPS = [
  { num: '01', title: 'Build your circles.', body: "Add the people who matter — old friends, current ones, people from every chapter of your life. Group them however makes sense to you." },
  { num: '02', title: 'Set your light.', body: "Green means you're doing well. Yellow means things are a little rough. Red means you need your people. Simple, honest, always in your control." },
  { num: '03', title: 'Check in.', body: "When someone's light changes — or they've gone quiet — tap to check in. They get a gentle nudge and respond with a thumbs up or down. No awkward texts. No pressure. Just: I see you." },
  { num: '04', title: 'Show up.', body: "Sometimes it becomes a call. Sometimes a long overdue catch-up. Sometimes it just quietly reminds someone they still have people — and that reminder is everything." },
]

export function AboutV2() {
  const { user } = useAuth()
  const [copied, setCopied] = useState(false)
  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share

  const handleNativeShare = async () => {
    try { await navigator.share({ title: 'The Beacon', text: SHARE_TEXT, url: ABOUT_URL }) } catch {}
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(ABOUT_URL)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }

  return (
    <div className="min-h-[100dvh] flex flex-col bg-gradient-to-br from-stone-900 via-stone-900 to-amber-950">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-orange-500/8 rounded-full blur-3xl" />
      </div>

      {/* Sticky nav */}
      <div className="sticky top-0 z-50 bg-stone-900/80 backdrop-blur border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3 max-w-lg mx-auto w-full">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
              <span className="text-base" role="img" aria-label="beacon">🔆</span>
            </div>
            <span className="text-white font-bold text-sm">The Beacon</span>
          </Link>
          <Link to="/auth?mode=signup" className="btn-primary text-sm px-4 py-2 font-semibold">
            Sign Up Free
          </Link>
        </div>
      </div>

      <div className="relative flex flex-col flex-1 max-w-lg mx-auto w-full px-6">

        {/* Hero */}
        <div className="flex flex-col items-center text-center pt-16 pb-12 gap-5">
          <div className="w-20 h-20 rounded-3xl bg-amber-500 flex items-center justify-center shadow-2xl shadow-amber-500/30">
            <span className="text-4xl" role="img" aria-label="beacon">🔆</span>
          </div>
          <div className="space-y-2">
            <h1 className="text-4xl font-bold text-white tracking-tight">The Beacon</h1>
            <p className="text-lg text-amber-300 font-medium">Stay loosely but meaningfully connected</p>
          </div>
        </div>

        {/* Opening */}
        <section className="py-10 border-t border-white/10">
          <h2 className="text-2xl font-bold text-white mb-5 leading-snug">
            We have never been more connected. And we have never felt more alone.
          </h2>
          <div className="space-y-4 text-stone-300 text-base leading-relaxed">
            <p>
              Social media gave us the performance of connection without the substance of it. We know what our friends had for lunch. We don't know if they're okay.
            </p>
            <p>
              The Beacon exists to change that.
            </p>
          </div>
        </section>

        {/* What it is */}
        <section className="py-10 border-t border-white/10">
          <h2 className="text-2xl font-bold text-white mb-4">What is The Beacon?</h2>
          <div className="space-y-3 text-stone-300 text-base leading-relaxed">
            <p>
              A free app for staying loosely but meaningfully connected with the people who matter — across every chapter of your life.
            </p>
            <p>
              A small, private circle. A gentle way to check in. No feed, no algorithm, no noise.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="py-10 border-t border-white/10">
          <h2 className="text-2xl font-bold text-white mb-6">How it works</h2>
          <div className="space-y-4">
            {STEPS.map(step => (
              <div key={step.num} className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10 flex gap-4">
                <span className="text-amber-500 font-bold text-sm shrink-0 mt-0.5">{step.num}</span>
                <div>
                  <p className="text-white font-semibold text-base mb-1">{step.title}</p>
                  <p className="text-stone-400 text-sm leading-relaxed">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Free always */}
        <section className="py-10 border-t border-white/10">
          <h2 className="text-2xl font-bold text-white mb-4">Free, always</h2>
          <p className="text-stone-300 text-base leading-relaxed">
            The Beacon is completely free — no subscriptions, no ads, no premium tiers. The people who need it most shouldn't have to pay for it.
          </p>
        </section>

        {/* CTA */}
        <section className="py-10 border-t border-white/10 space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-3 leading-snug">Be the reason someone feels seen.</h2>
            <div className="space-y-3 text-stone-300 text-base leading-relaxed">
              <p>
                A beacon only helps if someone is watching for the light. When you sign up, bring your circle with you. Just say:{' '}
                <span className="text-white font-medium italic">"I joined something. I want you in my circle."</span>
              </p>
              <p className="text-amber-300 font-medium">That message might matter more than you know.</p>
            </div>
          </div>

          <Link to="/auth?mode=signup" className="block w-full text-center btn-primary text-base font-bold py-4 shadow-lg shadow-amber-500/30">
            Sign Up Free
          </Link>

          {user && (
            <Link to="/dashboard" className="block w-full text-center py-3 rounded-2xl border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 transition-colors text-sm font-semibold">
              Invite a Friend from your Dashboard →
            </Link>
          )}

          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-white/10" />
            <span className="text-stone-500 text-xs">or spread the word</span>
            <div className="flex-1 border-t border-white/10" />
          </div>

          {canNativeShare && (
            <button onClick={handleNativeShare} className="w-full py-3 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2" type="button">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share with someone you care about
            </button>
          )}

          <div className="flex flex-wrap gap-2 justify-center">
            {PLATFORMS.map(p => (
              <a key={p.label} href={p.href} target={p.href.startsWith('sms') ? '_self' : '_blank'} rel="noopener noreferrer" className={`${p.bg} text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors`}>
                {p.label}
              </a>
            ))}
          </div>

          <button onClick={handleCopy} type="button" className="w-full py-3 rounded-2xl border border-white/10 text-stone-400 hover:text-white hover:border-white/20 transition-colors text-sm flex items-center justify-center gap-2">
            {copied ? (
              <><svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg><span className="text-green-400">Copied!</span></>
            ) : (
              <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>Copy link</>
            )}
          </button>
        </section>

        {/* Mission link */}
        <section className="py-8 border-t border-white/10">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center space-y-3">
            <p className="text-stone-400 text-sm italic">Want to understand why we built this?</p>
            <Link
              to="/mission"
              className="inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 font-semibold text-sm transition-colors"
            >
              Read our story
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </section>

        {/* 988 footer */}
        <section className="py-8 border-t border-white/10">
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-5 text-center space-y-2">
            <p className="text-stone-300 text-sm leading-relaxed">
              The Beacon is dedicated to suicide prevention through everyday human connection.
            </p>
            <p className="text-stone-300 text-sm leading-relaxed">
              If you or someone you know is in crisis, please{' '}
              <a href="tel:988" className="text-amber-400 font-semibold underline underline-offset-2">call or text 988</a>{' '}
              (Suicide &amp; Crisis Lifeline).
            </p>
          </div>
        </section>

        <div className="pb-10 text-center">
          <Link to="/" className="text-stone-600 hover:text-stone-400 text-xs transition-colors">- jointhebeacon.com</Link>
        </div>

      </div>
    </div>
  )
}

export default AboutV2
